import { useCallback } from 'react';

import { employeeIncidentService } from '@/api/domains/employeeIncident';
import type { IncidentStatus } from '@/api/domains/employeeIncident';
import { useHybridExport } from '@/hooks/ui/useHybridExport';
import { useTranslation } from '@/i18n';
import type { UIExportFormat } from '@/types/fileExport.types';


export interface UseIncidentManagementExportParams {
  dateFrom: string;
  dateTo: string;
  errorTypeId: number | null;
  status: IncidentStatus;
  filterEmployeeId: number | null;
  searchText: string;
}

export interface UseIncidentManagementExportResult {
  isExporting: boolean;
  exportIncidents: (format: UIExportFormat) => Promise<void>;
}

/**
 * Descarga de la gestión de incidencias a Excel. Estrategia híbrida sync/async: el backend responde
 * 200 con el archivo bajo el umbral síncrono, o 202 (job en background) cuando lo supera.
 *
 * Delega el branching sync/202, el estado `isExporting` y las notificaciones en `useHybridExport`;
 * aquí solo se arma el payload y se traducen los textos.
 */
export function useIncidentManagementExport(
  params: UseIncidentManagementExportParams,
): UseIncidentManagementExportResult {
  const { t: translate } = useTranslation();

  const requestExport = useCallback(
    () => employeeIncidentService.exportManagement({
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
      errorTypeId: params.errorTypeId,
      status: params.status === 'todos' ? null : params.status,
      filterEmployeeId: params.filterEmployeeId,
      searchText: params.searchText || null,
    }),
    [
      params.dateFrom,
      params.dateTo,
      params.errorTypeId,
      params.status,
      params.filterEmployeeId,
      params.searchText,
    ],
  );

  const { isExporting, runExport } = useHybridExport({
    requestExport,
    messages: {
      error: translate('attendance:incidentManagement.messages.exportError'),
      success: translate('attendance:incidentManagement.messages.exportSuccess'),
      queued: translate('attendance:incidentManagement.messages.exportQueued'),
    },
  });

  return { isExporting, exportIncidents: runExport };
}

export default useIncidentManagementExport;
